import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCandidateUsingIds, nonCandidateLoggedIn } from 'actions';
import useAuthGateState from 'hooks/useAuthGateState';
import { shouldFetchCandidateReportsById } from 'utils/candidateReportLoad';

/**
 * Bridge a restored `candidateLogin` session into `portal` state.
 *
 * `checkForActiveAuthSession` populates `candidateLogin`, but the pages that
 * decide whether someone is signed in read `portalAuth.loggedIn` (both are in
 * the AuthProvider Context). Layout
 * has always owned that bridge, which is fine for routes rendered inside it —
 * but the privacy login routes sit outside Layout, so without this a restored
 * session leaves the candidate sitting on the login form they are already
 * authenticated past.
 *
 * Shared by Layout and both privacy login routes so the two cannot drift.
 * The privacy routes pass `enabled` only for an inbound unified-login session,
 * because before unified login they never bridged a restored session.
 */
const useCandidateHydration = ({ enabled = true } = {}): void => {
  const dispatch = useDispatch();
  const authState = useAuthGateState();
  const {
    loggedIn: candidateLoggedIn,
    candidateJwt,
    candidateIds,
  } = authState.candidateLogin;
  const reportsProcessing = useSelector(
    (state: any) => state.portal?.processing,
  );
  const reportsFetched = useSelector(
    (state: any) => state.portal?.reportsFetched,
  );
  const portalLoggedIn = authState.portalAuth.loggedIn;

  // Once authenticated, fetch the candidate's data using their IDs.
  React.useEffect(() => {
    if (
      enabled &&
      shouldFetchCandidateReportsById({
        candidateLoggedIn,
        candidateJwt,
        candidateIds,
        reportsFetched,
        reportsProcessing,
      })
    ) {
      dispatch(fetchCandidateUsingIds({ candidateIds, candidateJwt }));
    }
  }, [
    enabled,
    candidateLoggedIn,
    candidateJwt,
    candidateIds,
    reportsFetched,
    reportsProcessing,
    dispatch,
  ]);

  // Non-candidate detection: authenticated but with no candidate records.
  // Covers both WorkOS (candidateJwt='') and Checkr Profile (candidateJwt set)
  // paths. Skips dispatch if portal already knows (e.g. processAuthResponse
  // dispatched nonCandidateLoggedIn eagerly).
  React.useEffect(() => {
    if (
      enabled &&
      candidateLoggedIn &&
      (!candidateIds || candidateIds.length === 0) &&
      !portalLoggedIn
    ) {
      dispatch(nonCandidateLoggedIn());
    }
  }, [enabled, candidateLoggedIn, candidateIds, portalLoggedIn, dispatch]);
};

export default useCandidateHydration;
